import { Component, EventEmitter, Injector, Input, OnInit, Output } from '@angular/core';
import { SessionBaseComponent } from '../../session.base.component';
import { LaasOption } from 'src/app/core/models/laas-option';
import { deepCopy, getCustomerByPermission, isNullOrEmpty } from 'src/app/core/utils/common-functions';
import { UserType } from 'src/app/core/enums/user-type.enum';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';
import { LogisticsCustomerService } from 'src/app/core/services/logistics/customer/logistics-customer.service';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NG_VALIDATORS, Validator, AbstractControl, ValidationErrors } from '@angular/forms';
@Component({
  selector: 'laas-customer-dropdown',
  templateUrl: './customer-dropdown.component.html',
  styleUrls: ['./customer-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: CustomerDropdownComponent,
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: CustomerDropdownComponent,
      multi: true
    }
  ]
})
export class CustomerDropdownComponent extends SessionBaseComponent implements OnInit, ControlValueAccessor, Validator {

  @Input() divClass: string = '';
  @Input() labelClass: string = '';
  @Input() dropdownStyle: any = {};
  @Input() showLabel: boolean = false;
  @Input() showClear: boolean = true;
  @Input() showFilter: boolean = true;
  @Input() permissionCode!: PermissionConstants;
  @Input() isSingle: boolean = false;
  @Input() isMultiple: boolean = false;
  @Input() showToggleAll: boolean = false;
  @Input() formControlName: string = '';
  @Input() showAllOptionItem: boolean = false;
  @Input() customerDataList: any[] = [];
  @Input() customerData: any;
  @Input() required: boolean = false;
  @Input() submitted: boolean = false;

  @Output() onOptionDataChange: EventEmitter<any> = new EventEmitter();
  @Output() onDataLoaded: EventEmitter<LaasOption[]> = new EventEmitter();
  private currentFilterKeyword: string = '';

  virtualScroll: boolean = false;
  customerOptions: LaasOption[] = [];
  customerOptionsByPermission: LaasOption[] = [];
  filteredCustomers: LaasOption[] = [];
  allOptionItem: LaasOption = {
    value: "ALL",
    label: "ALL - 全部"
  };
  onChangeFn: any = () => {};
  onTouchedFn: any = () => {};
  private innerValue: any;

  constructor(
    injector: Injector,
    private logisticsCustomerService: LogisticsCustomerService,
  ) {
    super(injector);
  }

  async ngOnInit(): Promise<void> {

    await super.ngOnInit();

    this.logisticsCustomerService.getCustomerOptions('1').subscribe({
      next: (result: any[]) => {

        this.customerOptions = deepCopy(result) ?? [];

        if (!this.permissionCode && !this.customerOptions) {
          this.customerOptionsByPermission = deepCopy(this.customerOptions) ?? [];
          if (this.showAllOptionItem) {
            this.customerOptionsByPermission.unshift(this.allOptionItem);
          }
          this.virtualScroll = this.customerOptionsByPermission && this.customerOptionsByPermission.length > 30;
          if (this.innerValue !== undefined) { this.assignValue(); }
          this.onDataLoaded.emit(this.customerOptionsByPermission);
          return;
        }

        const customerByPermission = getCustomerByPermission(this.userProfile, this.permissionCode);
        const isAllNull = customerByPermission.every((x: any) => isNullOrEmpty(x));
        const customerOptionsByPermission = result.filter((x: any) => this.userProfile.userType === UserType.SuperAdmin.toString() ||
          isAllNull || customerByPermission.includes(x.value));
        this.customerOptionsByPermission = customerOptionsByPermission.map((x: any) => ({ value: x.value, label: x.label }));
        this.filteredCustomers = [...this.customerOptionsByPermission];
        if (this.showAllOptionItem) {
          this.customerOptionsByPermission.unshift(this.allOptionItem);
        }
        // this.onDataLoaded.emit(this.customerOptionsByPermission);
        this.filteredCustomers = [...this.customerOptionsByPermission];
        this.virtualScroll = this.customerOptionsByPermission && this.customerOptionsByPermission.length > 30;
        if (this.innerValue !== undefined) {
          this.assignValue();
        }
        this.onDataLoaded.emit(this.customerOptionsByPermission);
        if (this.currentFilterKeyword) {
          this.onFilterInput(this.currentFilterKeyword);
        }
      },
    });

  }

  async onChange(event: any): Promise<void> {
    if (this.isMultiple) {
      this.customerDataList = event;
    } else {
      this.customerData = event;
    }

    this.onChangeFn(event);
    this.onTouchedFn();
    this.onOptionDataChange.emit(event);
  }

  onFilterInput(keyword: string) {
    this.currentFilterKeyword = keyword;
    if (!this.customerOptionsByPermission || this.customerOptionsByPermission.length === 0) {
    return;
    }
    if (!keyword) {
      this.filteredCustomers = [...this.customerOptionsByPermission];
      return;
    }
    const lower = keyword.toLowerCase();
    this.filteredCustomers = this.customerOptionsByPermission
      .filter(c => String(c.value).toLowerCase().includes(lower))
      .sort((a, b) => {
        const aVal = String(a.value).toLowerCase();
        const bVal = String(b.value).toLowerCase();
        const aStarts = aVal.startsWith(lower);
        const bStarts = bVal.startsWith(lower);
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        if (aVal.length !== bVal.length) return aVal.length - bVal.length;
        return aVal.localeCompare(bVal);
      });
  }

  writeValue(value: any): void {
    this.innerValue = value;
    if (this.filteredCustomers && this.filteredCustomers.length > 0) {
      this.assignValue();
    } else {
      if (this.isMultiple) {
        this.customerDataList = value ?? [];
      } else {
        this.customerData = value ?? null;
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  reset(): void {
    this.customerDataList = [];
    this.customerData = null;
    this.onChangeFn(this.isMultiple ? [] : null);
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (!this.required) return null;

    const val = control.value;
    const isEmpty = isNullOrEmpty(val) || (Array.isArray(val) && val.length === 0);
    return isEmpty ? { 'requiredCustomer': true } : null;
  }

  get isInvalid(): boolean {
    if (this.required && this.submitted) {
      const val = this.isMultiple ? this.customerDataList : this.customerData;
      return isNullOrEmpty(val) || (Array.isArray(val) && val.length === 0);
    }
    return false;
  }

  private assignValue() {
    const val = this.innerValue;
    if (this.isMultiple) {
      this.customerDataList = Array.isArray(val) ? val.map(v => String(v)) : [];
    } else {
      this.customerData = (val !== null && val !== undefined) ? String(val) : null;
    }

}
}
